import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Icon } from "../../ds/DS";
import { isOpen, useTicketDetail, type VTicket } from "../data";
import {
  BOARD_STATUSES,
  clientStatusLabel,
  clientStatusTone,
  fmtDateTime,
  shortAge,
} from "../tone";
import type { PageProps } from "../Portal";
import { PageHead, ScopeNote } from "./parts";
import { TicketInbox } from "./TicketInbox";
import { NewTicket, TicketThread } from "./ticketParts";
import { buildThread, id8, postTicketMessage } from "../ticketThread";
import { TWO_PANE_QUERY, useMedia } from "../useMedia";

// Support. Admins get the triage inbox (TicketInbox.tsx); clients get this
// two-pane view (handoff `showClientTickets`): waiting banner, Open / Waiting
// on you / Closed tabs, ticket cards on the left and the conversation on the
// right with a four-step progress strip. Client labels throughout; internal
// notes are never sent to clients by the API and never rendered here.

const WAITING = "Waiting on client";

const CLIENT_TABS: { id: string; label: string; fn: (t: VTicket) => boolean }[] = [
  { id: "open", label: "Open", fn: isOpen },
  { id: "you", label: "Waiting on you", fn: (t) => t.status === WAITING },
  { id: "closed", label: "Closed", fn: (t) => t.status === "Closed" },
];

const NEXT: Record<string, string> = {
  Open: "We've got it and will reply within one working day.",
  "In progress": "We're working on it. You'll get an update here and by email.",
  [WAITING]: "We need your answer to carry on. The clock is paused until you reply.",
  Closed: "Nothing. Reply if it comes back and we'll reopen it.",
};

function stepBar(i: number, idx: number, status: string): string {
  if (i < idx) return "var(--deep-teal)";
  if (i === idx) return status === WAITING ? "var(--status-warning)" : "var(--bright-teal)";
  return "var(--border-slate)";
}

/* ---------- Tickets page ---------- */
export function Tickets(props: PageProps) {
  if (props.isAdmin) return <TicketInbox {...props} />;
  return <ClientTickets {...props} />;
}

function ClientTickets({
  tickets,
  pid,
  setPid,
  projects,
  projectName,
  reload,
  toast,
  focus,
  clearFocus,
}: PageProps) {
  const scoped = useMemo(
    () =>
      (pid === "all" ? tickets : tickets.filter((t) => t.projectId === pid))
        .slice()
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [tickets, pid],
  );
  const [composing, setComposing] = useState(false);
  const [tab, setTab] = useState("open");
  const [sel, setSel] = useState<string | null>(null);
  // Narrow screens: the list, then the picked ticket in its place.
  const narrow = useMedia(TWO_PANE_QUERY);

  useEffect(() => setSel(null), [pid]);

  // Opened from elsewhere (Home, To do, a new ticket): select it on the right
  // tab. Waits for the ticket to arrive when it was only just created.
  useEffect(() => {
    if (focus?.page !== "tickets") return;
    const t = tickets.find((x) => x.id === focus.id);
    if (!t) return;
    setTab(t.status === WAITING ? "you" : t.status === "Closed" ? "closed" : "open");
    setSel(t.id);
    clearFocus();
  }, [focus, clearFocus, tickets]);

  const tabDef = CLIENT_TABS.find((x) => x.id === tab) ?? CLIENT_TABS[0];
  const list = tabDef ? scoped.filter(tabDef.fn) : scoped;
  const active =
    (sel ? scoped.find((t) => t.id === sel) : undefined) ?? (narrow ? null : list[0]) ?? null;
  const detailOnly = narrow && !!active;
  const waiting = scoped.filter((t) => t.status === WAITING).length;

  const { detail, refetch } = useTicketDetail(
    active?.projectId ?? null,
    active?.id ?? null,
  );
  const fresh = active && detail && detail.ticket.id === active.id ? detail : null;
  const items = useMemo(
    () => (active ? buildThread(active, fresh) : []),
    [active, fresh],
  );
  const idx = active ? Math.max(0, BOARD_STATUSES.indexOf(active.status)) : 0;

  return (
    <div className={"stack-24 sup" + (detailOnly ? " is-detail" : "")}>
      <PageHead
        eyebrow="TICKETS"
        title="Support"
        sub="Report a problem, ask a question or request a change. We reply within one working day, or one hour if something is down."
        actions={
          <Button icon="plus" onClick={() => setComposing(true)}>
            New ticket
          </Button>
        }
      />
      <ScopeNote pid={pid} setPid={setPid} projectName={projectName} />

      {waiting > 0 ? (
        <button className="sup-banner" onClick={() => setTab("you")}>
          <Icon name="hourglass" size={15} />
          <span>
            <strong>
              {waiting === 1
                ? "1 ticket is waiting on your reply."
                : `${waiting} tickets are waiting on your reply.`}
            </strong>{" "}
            We've paused the clock on these until you answer.
          </span>
          <span className="sup-banner-show">Show →</span>
        </button>
      ) : null}

      <div className={"sup-grid" + (detailOnly ? " is-detail" : "")}>
        {detailOnly ? null : (
          <div className="sup-left">
            <div className="sup-tabs" role="tablist">
              {CLIENT_TABS.map((x) => (
                <button
                  key={x.id}
                  role="tab"
                  aria-selected={tab === x.id}
                  className={"sup-tab" + (tab === x.id ? " is-on" : "")}
                  onClick={() => {
                    setTab(x.id);
                    setSel(null);
                  }}
                >
                  {x.label}
                  <span className="sup-tab-n mono">{scoped.filter(x.fn).length}</span>
                </button>
              ))}
            </div>
            <div className="sup-list">
              {list.map((t) => (
                <button
                  key={t.id}
                  className={"sup-card" + (active && t.id === active.id ? " is-on" : "")}
                  onClick={() => {
                    setSel(t.id);
                    if (narrow) window.scrollTo(0, 0);
                  }}
                >
                  <span className="sup-card-top">
                    <Badge tone={clientStatusTone(t.status)} dot>
                      {clientStatusLabel(t.status)}
                    </Badge>
                    <span className="mono sup-card-age">{shortAge(t.updatedAt)}</span>
                  </span>
                  <span className="sup-card-subject">{t.subject}</span>
                  <span className="mono sup-card-meta">
                    #{id8(t.id)} · {projectName(t.projectId)} · {t.author}
                  </span>
                </button>
              ))}
              {list.length === 0 ? <div className="sup-empty">Nothing here.</div> : null}
            </div>
          </div>
        )}

        {active ? (
          <div className="sup-conv">
            <div className="sup-rule" />
            {detailOnly ? (
              <button className="ibx-back" onClick={() => setSel(null)}>
                <Icon name="arrow-left" size={15} />
                All tickets
              </button>
            ) : null}
            <div className="sup-conv-head">
              <div>
                <div className="mono sup-conv-meta">
                  #{id8(active.id)} · {projectName(active.projectId)} · opened{" "}
                  {fmtDateTime(active.opened)}
                </div>
                <h2 className="sup-conv-title">{active.subject}</h2>
              </div>
              <div className="sup-steps">
                {BOARD_STATUSES.map((s, i) => (
                  <div key={s} className={"sup-step" + (i === idx ? " is-on" : "")}>
                    <div className="sup-step-bar" style={{ background: stepBar(i, idx, active.status) }} />
                    <span>{clientStatusLabel(s)}</span>
                  </div>
                ))}
              </div>
              <div className="sup-next">
                <Icon name="arrow-right" size={14} />
                <span>
                  <strong>What happens next.</strong> {NEXT[active.status] ?? NEXT.Open}
                </span>
              </div>
            </div>
            <div className="sup-thread">
              <TicketThread
                key={active.id}
                viewer="client"
                items={items}
                hint={
                  active.status === WAITING
                    ? "Replying restarts the clock."
                    : "We reply within one working day."
                }
                onSend={async (m) => {
                  // The server moves Waiting on client → In progress and
                  // reopens a Closed ticket on a client reply.
                  const r = await postTicketMessage(active, m);
                  setSel(active.id);
                  refetch();
                  reload();
                  toast(
                    r.boardStatus === "Open"
                      ? `Reply sent. ${active.subject} is reopened.`
                      : "Reply sent. We'll get back to you within one working day.",
                  );
                }}
              />
            </div>
          </div>
        ) : narrow ? null : (
          <div className="sup-conv sup-conv--empty">
            <Icon name="message-square" size={22} />
            <span>Pick a ticket to see the conversation.</span>
          </div>
        )}
      </div>

      <NewTicket
        open={composing}
        isAdmin={false}
        projects={projects}
        defaultPid={pid}
        onClose={() => setComposing(false)}
        onCreated={(id) => {
          reload();
          setTab("open");
          setSel(id);
          toast("Ticket raised. You'll get a reply within one working day.");
        }}
      />
    </div>
  );
}
